import { HStack } from "@chakra-ui/react";
import { AppWindow, ShieldAlert } from "lucide-react";
import { memo } from "react";

import MultiSelectFilter from "./components/MultiSelectFilter";
import {
  RISK_BAND_LABEL,
  type ResourceFilterOption,
  type ReviewListFilter,
  type RiskBand,
} from "./reviewLogic";

/**
 * ReviewResourceFilters — the reviewer-side campaign filter bar (PLO-4816):
 * filter a campaign's resource-review list by application/resource and by risk
 * band. Presentational + controlled — the parent (CampaignDetails) owns the
 * URL-backed filter state and passes the resource options; this renders the two
 * multi-selects and reports toggles/clears back up.
 */

// Risk-band swatch colours, aligned with the RiskBadge tints.
const RISK_SWATCH: Record<RiskBand, string> = {
  low: "success.500",
  medium: "warning.500",
  high: "error.500",
};

const RISK_OPTIONS = (["high", "medium", "low"] as RiskBand[]).map((band) => ({
  value: band,
  label: RISK_BAND_LABEL[band],
  swatch: RISK_SWATCH[band],
}));

export interface ReviewResourceFiltersProps {
  filter: ReviewListFilter;
  /** distinct resources present in this campaign's reviews */
  resourceOptions: ResourceFilterOption[];
  onToggleResource: (id: string) => void;
  onToggleRisk: (band: RiskBand) => void;
  onClearResources: () => void;
  onClearRisks: () => void;
}

const ReviewResourceFilters = memo(
  ({
    filter,
    resourceOptions,
    onToggleResource,
    onToggleRisk,
    onClearResources,
    onClearRisks,
  }: ReviewResourceFiltersProps) => {
    return (
      <HStack spacing={3} align="center" flexWrap="wrap">
        <MultiSelectFilter
          label="Application"
          icon={AppWindow}
          placeholder="All apps"
          options={resourceOptions.map((r) => ({
            value: r.id,
            label: r.name,
          }))}
          selected={filter.resourceIds}
          onToggle={onToggleResource}
          onClear={onClearResources}
        />
        <MultiSelectFilter
          label="Risk"
          icon={ShieldAlert}
          placeholder="All risk"
          options={RISK_OPTIONS}
          selected={filter.riskBands}
          onToggle={(v) => onToggleRisk(v as RiskBand)}
          onClear={onClearRisks}
        />
      </HStack>
    );
  },
);

ReviewResourceFilters.displayName = "ReviewResourceFilters";

export default ReviewResourceFilters;
